import {
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useState } from "react";
import { ProjectUpdate } from "../../hooks/useUpdates";

const typeLabels: Record<ProjectUpdate["type"], string> = {
  daily: "Daily",
  weekly: "Weekly",
  milestone: "Milestone",
};

const typeColors: Record<
  ProjectUpdate["type"],
  "default" | "primary" | "success"
> = {
  daily: "default",
  weekly: "primary",
  milestone: "success",
};

interface UpdateCardProps {
  update: ProjectUpdate;
  canDelete: boolean;
  onDelete?: (updateId: string) => Promise<void>;
}

export function UpdateCard({ update, canDelete, onDelete }: UpdateCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(update.id);
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Chip
                label={typeLabels[update.type]}
                size="small"
                color={typeColors[update.type]}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                {new Date(update.created_at).toLocaleString()}
              </Typography>
            </Stack>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {update.title}
            </Typography>
            {update.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
              >
                {update.description}
              </Typography>
            )}
          </Box>
          {canDelete && onDelete && (
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmOpen(true)}
              title="Delete update"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete update</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete "{update.title}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
